import { Inject, Injectable, Optional } from '@angular/core';

import { EnvironmentService } from '../environment/environment.service';
import { camelToKebapCase } from '../helpers/string-utils';
import { SERVER_REQUEST, ServerRequest } from '../server/server.tokens';
import { WindowRefService } from '../window-ref/window-ref.service';

@Injectable({
  providedIn: 'root'
})
export class BrowserService {
  private readonly win: Window;
  private readonly navigator: Navigator;
  private readonly userAgent: string;
  private readonly platform: string;
  private readonly ssrCachingEnabled: boolean;

  constructor(
    { nativeWindow }: WindowRefService,
    { ssrCachingEnabled }: EnvironmentService,
    @Optional() @Inject(SERVER_REQUEST) request: ServerRequest
  ) {
    this.win = nativeWindow;
    this.navigator = nativeWindow.navigator || ({ userAgent: '', platform: '' } as Navigator);
    this.userAgent = this.navigator.userAgent || request?.header('user-agent') || '';
    // navigator.platform is deprecated, however navigator.userAgentData.platform isn't fully supported yet.
    this.platform = this.navigator['platform'];
    this.ssrCachingEnabled = ssrCachingEnabled;

    this.applyBrowserClasses();
  }

  get isEdge(): boolean {
    return this.userAgent.includes('Edge/');
  }

  get isFirefox(): boolean {
    return this.userAgent.includes('Firefox/');
  }

  get isSafari(): boolean {
    return (
      typeof this.navigator.vendor !== 'undefined' &&
      this.navigator.vendor.startsWith('Apple') &&
      /\sSafari\//.test(this.userAgent)
    );
  }

  get isIos(): boolean {
    return /iPad|iPhone|iPod/.test(this.platform || this.userAgent);
  }

  get isAndroid(): boolean {
    return this.userAgent.includes('Android');
  }

  get isWindows(): boolean {
    return this.userAgent.includes('Windows') || this.platform.includes('Win32');
  }

  get isWindows7(): boolean {
    return this.userAgent.includes('Windows NT 6.1');
  }

  get isWindows10(): boolean {
    return this.userAgent.includes('Windows NT 10.0');
  }

  get isMac(): boolean {
    return this.platform.includes('Mac');
  }

  get isMobile(): boolean {
    return this.ssrCachingEnabled ? false : this.isIos || this.isAndroid;
  }

  private static getGetters(): (keyof BrowserService)[] {
    return [
      'isEdge',
      'isFirefox',
      'isSafari',
      'isIos',
      'isAndroid',
      'isWindows',
      'isWindows7',
      'isWindows10',
      'isMac',
      'isMobile'
    ];
  }

  private applyBrowserClasses(): void {
    const rootElement = this.win.document?.documentElement;
    // don't assign request-specific browser classes if SSR caching is on.
    if (!this.ssrCachingEnabled && rootElement) {
      const classes = BrowserService.getGetters()
        .filter(key => this[key])
        .map(key => camelToKebapCase(key));

      rootElement.classList.add(...classes);
    }
  }
}
